import useRouter from "@stadium/hooks/lib/use-custom-route";
import { useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";

import AuthLogin from "@components/auth/buttons/login";
import { InternalButton } from "@components/shared/buttons";

import { getCheckoutRoute } from "@constants/routes";
import usePopulateLocalItems from "@hooks/cart/use-populate-local-items";
import useOnCheckout from "@hooks/checkout/use-on-checkout";
import { updateTreatDetails } from "apis/order";
import { useSendItemsContext } from "@contexts/send-items-context";

import { getKitOrderSetupRoute } from "@constants/external-routes";
import {
  getCartOrderGiftDetailId,
  getCartOrderHasCustomizable,
  getCartOrderNumber,
  getCartOrderRecipientZones,
  getCartProductZoneList,
  getCartItemsCount,
  getCartOrderBagTotal,
  getIsCartUpdating,
  getReceivedItemsCount,
} from "@selectors/cart";

import { isCustomCurrency } from "@stadium/helpers/currency";
import { useAuth0V2 } from "@stadium/services/auth0";
import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";

import { useStoreContext } from "@components/store/provider";

import { useCartListingContext } from "@contexts/cart-listing-context";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useCheckoutContext as useGlobalCheckoutContext } from "@stadium/checkout/src/contexts/checkout";
import { useCheckoutContext } from "@contexts/checkout-context";
import { useWorkspaceContext } from "@contexts/workspace-context";

import { CHECKOUT_TARGET } from "@constants/analytics/cart";

import styles from "./main.module.scss";
import { getIsLockerShop, getIsPricelessShop } from "@selectors/store";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

const CartCheckoutCta = ({ ctaJuneSoProps = {}, className = "" }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const withAnalytics = useWithAnalytics();

  const { storeBasePath, displayCurrency, storeDisplayCurrencyCode } = useStoreContext();

  const { isAuthenticated } = useAuth0V2();
  const { isGuestOrPreviewMode, previewMode } = usePreviewModeContext();
  const { getDashboardUrlWithCurrentWorkspaceSlug } = useWorkspaceContext();
  const isLockerShop = useSelector(getIsLockerShop);
  const hasCustomizableItems = useSelector(getCartOrderHasCustomizable);
  const orderNumber = useSelector(getCartOrderNumber);
  const giftDetailId = useSelector(getCartOrderGiftDetailId);
  const recipientZones = useSelector(getCartOrderRecipientZones);
  const cartOrderNumber = useSelector(getCartOrderNumber);
  const cartProductZoneList = useSelector(getCartProductZoneList);
  const isCartUpdating = useSelector(getIsCartUpdating);
  const totalItems = useSelector(getCartItemsCount);
  const totalBagValue = useSelector(getCartOrderBagTotal);
  const isPricelessShop = useSelector(getIsPricelessShop);
  const receivedItemsCount = useSelector(getReceivedItemsCount);

  const { loading, updateOrder } = useOnCheckout();
  const { isSendToMyself } = useSendItemsContext();

  const { updateLocalCartCurrency } = usePopulateLocalItems();

  const [sendToOtherNextLoading, setSendToOtherNextLoading] = useState(false);

  const { cartItemsTotal, cartItemsQuantity, isAnyItemWithoutDigitalAsset } = useCheckoutContext();

  const { sendToOthersZoneWiseRecipient, sendToOtherItemName, variantSelectionError } =
    useCartListingContext();

  const { currency, useStadiumPoints, useStoreSpecificPoints, useWallet, selectedTeamWallet } =
    useGlobalCheckoutContext();

  const isCheckoutDisabled = (() => {
    if (
      isCartUpdating ||
      (cartItemsTotal === 0 && !isPricelessShop) ||
      (isPricelessShop && receivedItemsCount !== cartItemsQuantity)
    ) {
      return true;
    }

    if (isLockerShop && hasCustomizableItems) {
      return isAnyItemWithoutDigitalAsset;
    }

    const isVariantSelectionError = Object.values(variantSelectionError).some((value) => value);
    return !isGuestOrPreviewMode ? isVariantSelectionError : false;
  })();

  const redirectToCheckout = () => router.push(getCheckoutRoute({ storeBasePath, orderNumber }));

  const trackCheckoutEvent = () => {
    const methods = [];
    if (useStadiumPoints) methods.push("stadium_points");
    if (useStoreSpecificPoints) methods.push("store_specific_points");
    if (useWallet) methods.push("wallet");
    if (selectedTeamWallet?.id) methods.push("team_wallet");

    withAnalytics(undefined, {
      track: true,
      target: CHECKOUT_TARGET,
      eventProperties: {
        total_items: totalItems,
        total_bag_value: totalBagValue,
        selected_payment_methods: methods,
      },
    })();
  };

  const redirectToCheckoutPreviewMode = () => {
    trackCheckoutEvent();
    updateLocalCartCurrency(currency.id);
    redirectToCheckout();
  };

  const updateOrderDetails = () => {
    trackCheckoutEvent();
    updateOrder(
      {
        use_global_point: useStadiumPoints,
        use_store_specific_point: useStoreSpecificPoints,
        billed_currency_id: currency.id,
        use_wallet_money: useWallet,
        team_id: selectedTeamWallet?.id || "",
        use_team_point: !!selectedTeamWallet?.id,
      },
      redirectToCheckout,
    );
  };

  const getRecipientDetail = () => {
    let totalRecipients = 0;
    const recipientDetail = sendToOthersZoneWiseRecipient
      .filter(({ zoneId }) => cartProductZoneList.includes(zoneId))
      .map(({ zoneId, recipientCount }) => {
        const availableId = recipientZones.find(
          ({ zoneId: recipientZoneId }) => recipientZoneId === zoneId,
        );
        totalRecipients += +recipientCount;
        return {
          id: availableId?.id || "",
          zone_id: zoneId,
          count: +recipientCount,
        };
      });
    return { totalRecipients, recipientDetail };
  };

  const sendToOtherNextHandler = () => {
    const isRecipientCountInvalid = sendToOthersZoneWiseRecipient.some(
      ({ recipientCount }) => !recipientCount,
    );
    if (isRecipientCountInvalid) return;
    setSendToOtherNextLoading(true);
    const { totalRecipients, recipientDetail } = getRecipientDetail();
    dispatch(
      updateTreatDetails(orderNumber, {
        order: {
          gift_detail_attributes: {
            ...(giftDetailId ? { id: giftDetailId } : {}),
            expected_count: totalRecipients,
            recipient_zones_attributes: recipientDetail,
          },
          ...(!isCustomCurrency(storeDisplayCurrencyCode) && {
            billed_currency_id: displayCurrency?.id,
          }),
          bundle_name: sendToOtherItemName,
        },
      }),
    )
      .then(() => {
        window.location.href = getDashboardUrlWithCurrentWorkspaceSlug(
          getKitOrderSetupRoute(cartOrderNumber),
        );
      })
      .catch((err) => {
        throw err;
      })
      .finally(() => setSendToOtherNextLoading(false));
  };

  const renderSendToOthersNextCta = () => {
    if (isAuthenticated)
      return (
        <InternalButton
          onClick={sendToOtherNextHandler}
          isLoading={sendToOtherNextLoading}
          styleType="primary"
          className={styles.nextBtn}
          analytics={ctaJuneSoProps}
        >
          Next
        </InternalButton>
      );
    return (
      <AuthLogin loginOptions={{ signUp: true }}>
        <InternalButton
          disabled={isCheckoutDisabled}
          styleType="primary"
          analytics={ctaJuneSoProps}
        >
          Next
        </InternalButton>
      </AuthLogin>
    );
  };

  const renderProceedToCheckoutCta = () => {
    const ctaText = !isMobileDevice ? "Proceed to Checkout" : "Checkout";
    if (previewMode)
      return (
        <InternalButton
          onClick={redirectToCheckoutPreviewMode}
          isDisabled={isCheckoutDisabled}
          disabled={isCheckoutDisabled}
          styleType="primary"
          analytics={ctaJuneSoProps}
          className={className}
        >
          {ctaText}
        </InternalButton>
      );
    if (isAuthenticated)
      return (
        <InternalButton
          onClick={updateOrderDetails}
          isLoading={loading}
          disabled={isCheckoutDisabled}
          styleType="primary"
          analytics={ctaJuneSoProps}
          className={className}
        >
          {ctaText}
        </InternalButton>
      );
    return (
      <AuthLogin loginOptions={{ signUp: true }}>
        <InternalButton
          disabled={isCheckoutDisabled}
          styleType="primary"
          analytics={ctaJuneSoProps}
          className={className}
        >
          {ctaText}
        </InternalButton>
      </AuthLogin>
    );
  };

  return isSendToMyself ? renderProceedToCheckoutCta() : renderSendToOthersNextCta();
};

export default CartCheckoutCta;
